import { useMutation } from "@tanstack/react-query";
import {
  boomreportWebhookEvents,
  boomscreenWebhookEvents,
  WebhookEvent,
  webhookEvents
} from "../types";
import { getErrorMessage } from "utils/error";

import { instance } from "api/apiBase";
import { useForm } from "shared/hooks";
import { validateSchema } from "../schemas";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Input,
  Modal,
  Select
} from "shared/components";
import { toast } from "react-toastify";
import { useAuthContext } from "providers/Auth";
import { useMemo } from "react";

export const AddWebhookModal = ({
  open,
  setOpen,
  onSuccess
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess: () => void;
}) => {
  const { type } = useAuthContext();

  const defaultWebhookEvent = type.isBoomScreen
    ? WebhookEvent.ApplicationApproved
    : WebhookEvent.CustomerRegistered;

  const { mutate, isPending } = useMutation({
    mutationFn: (data: {
      url: string;
      event_name: WebhookEvent;
      token?: string;
    }) => {
      return instance.post("/portal/v1/webhooks", data).catch((err) => {
        toast.error("Error creating webhook: " + getErrorMessage(err));
      });
    },
    mutationKey: ["WEBHOOKS"],
    onSuccess: () => {
      onSuccess();
      setOpen(false);
    }
  });

  const { register, onSubmit } = useForm(
    {
      endpoint: "",
      event: defaultWebhookEvent,
      token: ""
    },
    validateSchema
  );

  const options = useMemo(() => {
    if (type.isBoomScreen && type.isSDK)
      return Object.entries(webhookEvents).map(([key, value]) => ({
        label: value,
        value: key
      }));

    if (type.isBoomScreen)
      return Object.entries(boomscreenWebhookEvents).map(([key, value]) => ({
        label: value,
        value: key
      }));

    if (type.isSDK)
      return Object.entries(boomreportWebhookEvents).map(([key, value]) => ({
        label: value,
        value: key
      }));

    return [];
  }, [type]);

  return (
    <Modal
      opened={open}
      onClose={() => setOpen(false)}
      panelClassName="p-6 max-w-[480px] w-full"
    >
      <h1 className="text-xl font-bold text-content-1000 mb-6">New webhook</h1>
      <form
        onSubmit={onSubmit({
          onSuccess: (data) => {
            mutate({
              url: data.endpoint,
              event_name: data.event,
              token: data.token ? data.token : undefined
            });
          }
        })}
      >
        <Select
          className="mb-4"
          label="Event"
          options={options}
          hideOptional
          defaultValue={defaultWebhookEvent}
          id="event"
          {...register("event")}
        />

        <Input
          id="endpoint"
          label="Endpoint URL"
          placeholder="https://api.example.com"
          hideOptional
          {...register("endpoint")}
        />

        <Input id="token" label="Secret" {...register("token")} />
        <span className="text-xs text-content-300">
          This secret will be sent with each webhook request
        </span>

        <Button
          size={ButtonSize.BASE}
          variant={ButtonVariant.PRIMARY}
          className="mt-4 max-w-[125px] ml-auto w-full"
          type="submit"
          loading={isPending}
          disabled={isPending}
        >
          Save
        </Button>
      </form>
    </Modal>
  );
};
